import React, { useCallback, useMemo, useState } from 'react';
import { DropDown } from '../components/commons/DropDown';
import {
  Error,
  errorBgColorTailwind,
  Errors,
} from '../components/commons/Error';
import {
  TargetDateValidateError,
  targetDateErrorToString,
} from 'components/features/target-achievement-support/utils/validate';
import { range } from 'utils/arrays';

const Switch = ({
  name,
  onClick,
}: {
  name: string;
  onClick: () => void;
}): React.ReactElement => {
  return (
    <>
      <div
        className="flex flex-row items-center text-bluegray-500 cursor-pointer"
        onClick={onClick}>
        <div className="icon-repeat"></div>
        <div className="ml-2 text-xs">{name}</div>
      </div>
    </>
  );
};

type AgeOrYear = 'Age' | 'Year';

const useAgeOrYear = (
  birthYear: number,
  currentAge: number,
  initialValueYear?: number
): {
  Switch: () => React.ReactElement;
  ageOrYear: AgeOrYear;
  setValue: (value: ((prev: number) => number) | number) => void;
  value: number;
  absYear: number;
} => {
  const [ageOrYear, setAgeOrYear] = useState<AgeOrYear>('Year');
  const YEAR = Symbol('Year');
  const thisYear = new Date().getFullYear();
  const [abs, _setAbs] = useState<{
    tag: typeof YEAR;
    value: number;
  }>(
    initialValueYear
      ? { tag: YEAR, value: initialValueYear }
      : {
          tag: YEAR,
          value: thisYear + 20,
        }
  );
  const setAbs = useCallback(
    (value: number | ((prev: number) => number)): void => {
      _setAbs(prev => ({
        ...prev,
        value: Math.floor(
          typeof value === 'function' ? value(prev.value) : value
        ),
      }));
    },
    []
  );

  const value = ((absValue: number): number => {
    switch (ageOrYear) {
      case 'Age':
        return absValue - birthYear;
      case 'Year':
        return absValue - thisYear;
    }
  })(abs.value);
  const setYear = useCallback(
    (year: number | ((prev: number) => number)): void =>
      setAbs(
        prev => thisYear + (typeof year === 'function' ? year(prev) : year)
      ),
    [thisYear, setAbs]
  );
  const setAge = useCallback(
    (age: number | ((prev: number) => number)): void =>
      setAbs(prev => birthYear + (typeof age === 'function' ? age(prev) : age)),
    [birthYear, setAbs]
  );
  const setValue = useCallback(
    (value: number | ((prev: number) => number)): void => {
      switch (ageOrYear) {
        case 'Age':
          return setAge(value);
        case 'Year':
          return setYear(value);
      }
    },
    [ageOrYear, setAge, setYear]
  );
  const Switch_ = (): React.ReactElement => {
    const isCentenarian = currentAge > 100;
    return (
      <>
        {isCentenarian ? (
          <></>
        ) : (
          <Switch
            {...{
              name: `${ageOrYear === 'Age' ? '期間' : '年齢'}で入力`,
              onClick: (): void =>
                setAgeOrYear(prev => (prev === 'Age' ? 'Year' : 'Age')),
            }}
          />
        )}
      </>
    );
  };
  return {
    ageOrYear,
    Switch: Switch_,
    value,
    setValue,
    absYear: abs.value,
  };
};

export const useTargetAgeOrYearForm = ({
  birthday,
  dateErrors,
  initialValueYear,
}: {
  birthday: Date;
  dateErrors: TargetDateValidateError[];
  initialValueYear?: number;
}): {
  TargetAgeOrYearForm: React.ReactElement;
  absYear: number;
  Switch: () => React.ReactElement;
  value: number;
  isDateError: boolean;
} => {
  const isDateError = !!dateErrors.length;
  const currentAge = new Date().getFullYear() - birthday.getFullYear() + 1;
  const { Switch, ageOrYear, setValue, value, absYear } = useAgeOrYear(
    birthday.getFullYear(),
    currentAge,
    initialValueYear
  );

  const states = ageOrYear === 'Age' ? range(currentAge, 100) : range(1, 80);

  const toString = (val: number): string => `${val.toString()}`;
  const toLabel = (val: number): string => `${val}`;
  const fromString = (
    val: string
  ): { tag: 'Just'; value: number } | { tag: 'Nothing' } => {
    if (!val) return { tag: 'Nothing' };
    const num = Number(val);
    return { tag: 'Just', value: num };
  };

  const TargetAgeOrYearForm = useMemo((): React.ReactElement => {
    return (
      <>
        <div>
          <div className="flex flex-row items-end">
            <div
              className={`flex flex-row items-center h-10 rounded-lg w-full border px-4 ${isDateError &&
                `bg-${errorBgColorTailwind}`} border-bluegray-400`}>
              <DropDown
                {...{
                  classNames: {
                    container: prev => `flex flex-row overflow-hidden w-full`,
                    select: prev => `${prev} text-base`,
                    dropdownIcon: prev =>
                      `text-bluegray-500 pointer-events-none icon-chevron-down p-0.5 ml-auto`,
                  },
                  dropdownIcon: 'chevron-down',
                  toString_: toString,
                  fromString,
                  setState: setValue,
                  state: value,
                  states,
                  toLabel,
                }}
              />
            </div>
            <div className="whitespace-no-wrap ml-2 text-xs text-bluegray-700">
              {((): string => {
                switch (ageOrYear) {
                  case 'Age':
                    return '歳';
                  case 'Year':
                    return '年後';
                }
              })()}
            </div>
          </div>
          {isDateError && (
            <Errors className="mt-1">
              {dateErrors.map(e => (
                <Error
                  {...{
                    error: e,
                    errorToString: targetDateErrorToString(
                      birthday.getFullYear()
                    )(ageOrYear),
                  }}
                  key={e}
                />
              ))}
            </Errors>
          )}
        </div>
      </>
    );
  }, [value, setValue, states, isDateError, dateErrors, ageOrYear, birthday]);
  return {
    TargetAgeOrYearForm,
    Switch,
    value,
    absYear,
    isDateError,
  };
};
